import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { 
  Bell, 
  AlertTriangle, 
  CheckCircle, 
  Info, 
  Calendar, 
  Users, 
  DollarSign,
  Filter,
  Search,
  MoreVertical,
  Eye,
  Trash2
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/form-input'
import { Badge } from '@/components/ui/badge'

interface Notification {
  id: string
  title: string
  message: string
  type: 'success' | 'warning' | 'error' | 'info'
  category: 'system' | 'payment' | 'student' | 'academic'
  timestamp: string
  isRead: boolean
  priority: 'high' | 'medium' | 'low'
  actionRequired?: boolean
}

const CollegeNotifications = () => {
  const [selectedFilter, setSelectedFilter] = useState<string>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [readFilter, setReadFilter] = useState<string>('all')

  const notifications: Notification[] = [
    {
      id: '1',
      title: 'Fee Payment Reminder',
      message: '25 students have pending fee payments due in 3 days',
      type: 'warning',
      category: 'payment',
      timestamp: '2025-06-15T10:30:00Z',
      isRead: false,
      priority: 'high',
      actionRequired: true
    },
    {
      id: '2',
      title: 'New Student Enrollment',
      message: '5 new students enrolled in Computer Science program',
      type: 'success',
      category: 'student',
      timestamp: '2025-06-15T09:15:00Z',
      isRead: false,
      priority: 'medium'
    },
    {
      id: '3',
      title: 'Monthly Fee Collection Target',
      message: 'Congratulations! Monthly collection target achieved 95%',
      type: 'success',
      category: 'payment',
      timestamp: '2025-06-15T08:00:00Z',
      isRead: true,
      priority: 'medium'
    },
    {
      id: '4',
      title: 'System Maintenance Scheduled',
      message: 'Fee management system will be under maintenance tomorrow 2-4 AM',
      type: 'info',
      category: 'system',
      timestamp: '2025-06-14T16:45:00Z',
      isRead: true,
      priority: 'low'
    },
    {
      id: '5',
      title: 'Exam Schedule Updated',
      message: 'Mid-semester exam dates have been updated. Check academic calendar.',
      type: 'info',
      category: 'academic',
      timestamp: '2025-06-14T14:30:00Z',
      isRead: false,
      priority: 'medium'
    },
    {
      id: '6',
      title: 'Fee Defaulter Alert',
      message: '12 students have overdue payments exceeding 30 days',
      type: 'error',
      category: 'payment',
      timestamp: '2025-06-14T11:20:00Z',
      isRead: false,
      priority: 'high',
      actionRequired: true
    },
    {
      id: '7',
      title: 'Staff Meeting Reminder',
      message: 'Monthly staff meeting scheduled for tomorrow at 11:00 AM',
      type: 'info',
      category: 'academic',
      timestamp: '2025-06-13T17:00:00Z',
      isRead: true,
      priority: 'low'
    },
    {
      id: '8',
      title: 'Database Backup Completed',
      message: 'Weekly database backup completed successfully',
      type: 'success',
      category: 'system',
      timestamp: '2025-06-13T02:00:00Z',
      isRead: true,
      priority: 'low'
    }
  ]

  const categories = [
    { id: 'all', label: 'All', count: notifications.length },
    { id: 'payment', label: 'Payment', count: notifications.filter(n => n.category === 'payment').length },
    { id: 'student', label: 'Student', count: notifications.filter(n => n.category === 'student').length },
    { id: 'academic', label: 'Academic', count: notifications.filter(n => n.category === 'academic').length },
    { id: 'system', label: 'System', count: notifications.filter(n => n.category === 'system').length }
  ]

  // Filter notifications
  const filteredNotifications = notifications.filter(notification => {
    const matchesCategory = selectedFilter === 'all' || notification.category === selectedFilter
    const matchesSearch = searchTerm === '' || 
      notification.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      notification.message.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesRead = readFilter === 'all' || 
      (readFilter === 'unread' && !notification.isRead) ||
      (readFilter === 'read' && notification.isRead)
    
    return matchesCategory && matchesSearch && matchesRead
  })

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-600" />
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-yellow-600" />
      case 'error':
        return <AlertTriangle className="h-5 w-5 text-red-600" />
      case 'info':
        return <Info className="h-5 w-5 text-blue-600" />
      default:
        return <Bell className="h-5 w-5 text-gray-600" />
    }
  }

  const getNotificationBgColor = (type: string) => {
    switch (type) {
      case 'success':
        return 'bg-green-50 border-green-200'
      case 'warning':
        return 'bg-yellow-50 border-yellow-200'
      case 'error':
        return 'bg-red-50 border-red-200'
      case 'info':
        return 'bg-blue-50 border-blue-200'
      default:
        return 'bg-gray-50 border-gray-200'
    }
  }

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'payment':
        return 'bg-green-100 text-green-800'
      case 'student':
        return 'bg-blue-100 text-blue-800'
      case 'academic':
        return 'bg-purple-100 text-purple-800'
      case 'system':
        return 'bg-orange-100 text-orange-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800'
      case 'medium':
        return 'bg-yellow-100 text-yellow-800'
      case 'low':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60)
    
    if (diffInHours < 1) {
      return 'Just now'
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)} hours ago`
    } else {
      return date.toLocaleDateString('en-IN')
    }
  }

  const unreadCount = notifications.filter(n => !n.isRead).length
  const highPriorityCount = notifications.filter(n => n.priority === 'high' && !n.isRead).length
  const actionRequiredCount = notifications.filter(n => n.actionRequired && !n.isRead).length

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Notifications</h1>
          <p className="text-gray-600">Stay updated with college activities and alerts</p>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button variant="outline">
            Mark all as read
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Unread</p>
                <p className="text-2xl font-bold text-red-600">{unreadCount}</p>
              </div>
              <Bell className="h-8 w-8 text-red-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">High Priority</p>
                <p className="text-2xl font-bold text-orange-600">{highPriorityCount}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Action Required</p>
                <p className="text-2xl font-bold text-blue-600">{actionRequiredCount}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total</p>
                <p className="text-2xl font-bold text-gray-900">{notifications.length}</p>
              </div>
              <Info className="h-8 w-8 text-gray-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedFilter(category.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedFilter === category.id
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category.label} ({category.count})
                </button>
              ))}
            </div>

            {/* Read Status Filter */}
            <div className="flex gap-2">
              {['all', 'unread', 'read'].map((status) => (
                <button
                  key={status}
                  onClick={() => setReadFilter(status)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    readFilter === status
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                type="text"
                placeholder="Search notifications..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notifications List */}
      <div className="space-y-4">
        {filteredNotifications.map((notification, index) => (
          <motion.div
            key={notification.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
          >
            <Card className={`${getNotificationBgColor(notification.type)} ${
              !notification.isRead ? 'border-l-4 border-l-green-500' : ''
            }`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    <div className="p-2 bg-white rounded-lg">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className={`font-semibold ${!notification.isRead ? 'text-gray-900' : 'text-gray-700'}`}>
                          {notification.title}
                        </h3>
                        {!notification.isRead && (
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        )}
                      </div>
                      <p className="text-gray-600 mb-3">{notification.message}</p>
                      <div className="flex items-center space-x-2">
                        <Badge 
                          variant="outline" 
                          className={getCategoryColor(notification.category)}
                        >
                          {notification.category}
                        </Badge>
                        <Badge 
                          variant="outline" 
                          className={getPriorityColor(notification.priority)}
                        >
                          {notification.priority} priority
                        </Badge>
                        {notification.actionRequired && (
                          <Badge variant="outline" className="bg-red-100 text-red-800">
                            Action Required
                          </Badge>
                        )}
                        <span className="text-sm text-gray-500">
                          {formatTimestamp(notification.timestamp)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button variant="ghost" size="sm">
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {filteredNotifications.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <Bell className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">
              No notifications found matching your criteria.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

export default CollegeNotifications
